import React, { ReactElement, useMemo, useState } from "react"
import { Typography, Link, useTheme, Stack, Divider } from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import { useHistory, Link as RouterLink } from "react-router-dom"
import { getShortJourneyLabel } from "../hooks/useJourneyOptions"
import useCarrotPlanEmployeeSettings from "../hooks/useCarrotPlanEmployeeSettings"
import { useStartRouting } from "../shared/useQueryStartRouting"
import { CarrotPlansPaths } from "#/utils/Paths"
import { JourneyChangeDialogContainer } from "./JourneyChangeDialog"
import { useJourneyDisplay } from "../hooks/useJourneyDisplay"
import { useGetInProgressCarrotPlanRequestType } from "#/components/carrot-plans/hooks/useGetInProgressCarrotPlanRequestType"
import { RoutingFlowType } from "#/components/carrot-plans/types/routingFlowType"
import { CheckInTimeIntroContainer } from "#/components/carrot-plans/check-in/CheckInTimeIntro"
import { Journey } from "#/components/carrot-plans/types/journey"
import useCarrotPlan from "#/components/carrot-plans/hooks/useCarrotPlan"
import { isPlanOlderThan7Days } from "#/components/carrot-plans/shared/calculateIsPlanOlderThan7Days"

export const sensitiveCheckInJourneys = [
  Journey.ADOPTION,
  Journey.ASSISTED_REPRODUCTION,
  Journey.GESTATIONAL,
  Journey.PREGNANT,
  Journey.TRY_PREGNANT
]

export function JourneyDisplay(): ReactElement {
  const {
    shouldShowPreCarrotPlanJourneyDisplay,
    shouldShowPostCarrotPlanJourneyDisplay,
    journeyDataIsLoaded,
    resolvedJourney,
    carrotPlanIsOnTheWay,
    isMemberInSensitiveState
  } = useJourneyDisplay()
  const inProgressCarrotPlanRequestTypeQuery = useGetInProgressCarrotPlanRequestType()
  const showContinueRequestLink = inProgressCarrotPlanRequestTypeQuery.data != null

  return useMemo(() => {
    if (isMemberInSensitiveState && !carrotPlanIsOnTheWay) {
      return null
    }

    if (shouldShowPreCarrotPlanJourneyDisplay && journeyDataIsLoaded) {
      return <PreCarrotPlanJourneyDisplay journeySelection={resolvedJourney} />
    }

    if (carrotPlanIsOnTheWay && journeyDataIsLoaded) {
      return <PendingCarrotPlanJourneyDisplay journeySelection={resolvedJourney} />
    }

    if (shouldShowPostCarrotPlanJourneyDisplay && journeyDataIsLoaded) {
      return (
        <PostCarrotPlanJourneyDisplay
          journeySelection={resolvedJourney}
          showContinueRequestLink={showContinueRequestLink}
        />
      )
    }

    return null
  }, [
    isMemberInSensitiveState,
    journeyDataIsLoaded,
    carrotPlanIsOnTheWay,
    shouldShowPostCarrotPlanJourneyDisplay,
    shouldShowPreCarrotPlanJourneyDisplay,
    resolvedJourney,
    showContinueRequestLink
  ])
}

type PreCarrotPlanJourneyDisplayProps = {
  journeySelection: string
}

function PreCarrotPlanJourneyDisplay({ journeySelection }: PreCarrotPlanJourneyDisplayProps): ReactElement {
  const theme = useTheme()
  const history = useHistory()
  const { isDoneForNow } = useCarrotPlanEmployeeSettings()
  const { startRouting } = useStartRouting(RoutingFlowType.CASE_RATE_RENEWAL)

  async function sendToRouting(): Promise<Promise<void> | void> {
    if (isDoneForNow) {
      await startRouting()
    } else {
      history.push(CarrotPlansPaths.ENROLLMENT)
    }
  }

  return (
    <Stack direction="row" spacing={theme.spacing(theme.tokens.spacing.xs)}>
      {journeySelection ? <JourneySelection journeySelection={journeySelection} /> : <ReadyToUnlockYourBenefit />}
      <Link component="button" onClick={sendToRouting}>
        {journeySelection ? (
          <FormattedMessage defaultMessage="Tell us about your journey to unlock your benefit" />
        ) : (
          <FormattedMessage defaultMessage="Tell us about your journey" />
        )}
      </Link>
    </Stack>
  )
}

type PostCarrotPlanJourneyDisplayProps = {
  journeySelection: string
  showContinueRequestLink: boolean
}

function PostCarrotPlanJourneyDisplay({
  journeySelection,
  showContinueRequestLink
}: PostCarrotPlanJourneyDisplayProps): ReactElement {
  const theme = useTheme()

  return (
    <Stack direction="row" spacing={theme.spacing(theme.tokens.spacing.xs)}>
      <JourneySelection journeySelection={journeySelection} showDivider={true} />
      {showContinueRequestLink ? (
        <ContinueRoutingRequestLink />
      ) : (
        <JourneyUpdateActions journeySelection={journeySelection} />
      )}
    </Stack>
  )
}

type PendingCarrotPlanJourneyDisplayProps = {
  journeySelection: string
}

function PendingCarrotPlanJourneyDisplay({ journeySelection }: PendingCarrotPlanJourneyDisplayProps): ReactElement {
  const theme = useTheme()

  return (
    <Stack direction="row" spacing={theme.spacing(theme.tokens.spacing.xs)}>
      <JourneySelection journeySelection={journeySelection} showDivider={false} />
    </Stack>
  )
}

type JourneySelectionProps = {
  journeySelection: string
  showDivider?: boolean
}

function JourneySelection({ journeySelection, showDivider = true }: JourneySelectionProps): ReactElement {
  const theme = useTheme()
  const intl = useIntl()
  const journeyLabel = getShortJourneyLabel(journeySelection)
  return journeyLabel ? (
    <Stack direction="row" spacing={theme.spacing(theme.tokens.spacing.xs)}>
      <Typography>
        <FormattedMessage defaultMessage="Your journey:" /> {intl.formatMessage(journeyLabel)}
      </Typography>
      {showDivider && <Divider orientation="vertical" />}
    </Stack>
  ) : null
}

function ReadyToUnlockYourBenefit(): ReactElement {
  return (
    <Typography>
      <FormattedMessage defaultMessage="Ready to unlock your benefit?" />
    </Typography>
  )
}

function ContinueRoutingRequestLink(): ReactElement {
  return (
    <Link to={CarrotPlansPaths.REDIRECT} component={RouterLink}>
      <FormattedMessage defaultMessage="Continue sharing your update" />
    </Link>
  )
}

function JourneyUpdateActions({ journeySelection }: { journeySelection: string }): ReactElement {
  const [isJourneyChangeModalOpen, setIsJourneyChangeModalOpen] = useState(false)
  const [isCheckInModalOpen, setIsCheckInModalOpen] = useState(false)

  const carrotPlanQuery = useCarrotPlan()
  const showUpdateUsCheckInLink =
    sensitiveCheckInJourneys.includes(journeySelection as Journey) &&
    isPlanOlderThan7Days(carrotPlanQuery.data?.createdAtUtc)
  const theme = useTheme()

  const handleOpenModal = (event: React.MouseEvent, setOpen: (open: boolean) => void) => {
    event.preventDefault()
    setOpen(true)
  }

  return (
    <>
      <Stack direction="row" spacing={theme.spacing(theme.tokens.spacing.xs)}>
        <Link component="button" onClick={(e) => handleOpenModal(e, setIsJourneyChangeModalOpen)}>
          <FormattedMessage defaultMessage="Change journey" />
        </Link>
        {showUpdateUsCheckInLink && (
          <>
            <Divider orientation="vertical" />
            <Link component="button" onClick={(e) => handleOpenModal(e, setIsCheckInModalOpen)}>
              <FormattedMessage defaultMessage="Update us" />
            </Link>
          </>
        )}
      </Stack>
      {isJourneyChangeModalOpen && (
        <JourneyChangeDialogContainer origin="cta" setToggleModalFromCta={setIsJourneyChangeModalOpen} />
      )}
      {isCheckInModalOpen && <CheckInTimeIntroContainer origin="cta" setToggleModalFromCta={setIsCheckInModalOpen} />}
    </>
  )
}
